import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useRefereeAuth } from '../contexts/RefereeContext';
import { useNotification } from '../hooks/useNotification';
import NotificationContainer from '../components/common/NotificationContainer';
import RefereeList from '../components/referee/RefereeList';
import { RefereeDto } from '../services/refereeService';
import { FieldNewsDto } from '../services/fieldNewsService';
import CreateFieldNewsModal, { CreateNewsData } from '../components/field/CreateFieldNewsModal';
import NewsDetailModal from '../components/field/NewsDetailModal';
import GamesSection from '../components/field/GamesSection';
import FieldMapsSection from '../components/field/FieldMapsSection';
import FieldInfoButton from '../components/field/FieldInfoButton';
import { WeeklyCalendar } from '../components/schedule';
import ConfirmModal from '../components/common/ConfirmModal';
import { useConfirmModal } from '../hooks/useConfirmModal';
import {
  mainRefereeOverviewInfo,
  mainRefereeNewsInfo,
  mainRefereeGamesInfo,
  mainRefereeRefereesInfo,
} from '../components/field/mainRefereeInfo';
import mainRefereeService from '../services/mainRefereeService';
import { useSubscription } from '../contexts/SubscriptionContext';

const Background = styled.div`
  min-height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  padding: 80px 20px 20px 20px;
  color: white;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 20px;
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Title = styled.h1`
  color: #00d4ff;
  font-size: 2.5rem;
  font-weight: bold;
  margin: 0;
  text-transform: uppercase;
  font-family: 'Courier New', monospace;

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const Subtitle = styled.div`
  color: #4caf50;
  font-size: 1rem;
  font-weight: bold;
  text-transform: uppercase;
  font-family: 'Courier New', monospace;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BackButton = styled.button`
  background: transparent;
  border: 1px solid rgba(0, 212, 255, 0.5);
  color: #00d4ff;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Courier New', monospace;

  &:hover {
    background: rgba(0, 212, 255, 0.1);
    border-color: #00d4ff;
  }
`;

const TabContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0;
  margin-bottom: 25px;
  border-bottom: 2px solid rgba(0, 212, 255, 0.2);
`;

const TabButton = styled.button<{ $active: boolean }>`
  background: ${props => props.$active ? 'rgba(0, 212, 255, 0.15)' : 'transparent'};
  border: none;
  border-bottom: 3px solid ${props => props.$active ? '#00d4ff' : 'transparent'};
  color: ${props => props.$active ? '#00d4ff' : '#b0b0b0'};
  padding: 15px 25px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  font-family: 'Courier New', monospace;
  letter-spacing: 1px;

  &:hover {
    color: #00d4ff;
    background: rgba(0, 212, 255, 0.08);
  }

  @media (max-width: 768px) {
    padding: 12px 16px;
    font-size: 0.85rem;
    flex: 1;
    text-align: center;
  }
`;

const SectionCard = styled.div`
  background: #101d2c;
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 16px;
  margin-bottom: 25px;
  overflow: hidden;
  box-shadow: 0 18px 32px rgba(0, 0, 0, 0.5);
`;

const SectionHeader = styled.div`
  background: #132235;
  border-bottom: 1px solid rgba(0, 212, 255, 0.25);
  padding: 18px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SectionTitle = styled.h3`
  color: #00d4ff;
  margin: 0;
  font-size: 1.2rem;
  font-weight: bold;
`;

const SectionContent = styled.div`
  padding: 24px;
`;

const ActionButton = styled.button`
  background: #00d4ff;
  border: 1px solid #00d4ff;
  color: #00121f;
  padding: 10px 20px;
  border-radius: 10px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;

  &:hover {
    background: #00e0ff;
    box-shadow: 0 0 15px rgba(0, 212, 255, 0.45);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const NewsItem = styled.div<{ $isPriority?: boolean }>`
  background: ${props => props.$isPriority ? '#251a08' : '#101d2c'};
  border: 1px solid ${props => props.$isPriority ? 'rgba(255, 170, 0, 0.5)' : 'rgba(0, 212, 255, 0.3)'};
  border-radius: 12px;
  padding: 22px;
  margin-bottom: 20px;
  position: relative;
  transition: all 0.3s ease;
  cursor: pointer;

  ${props => props.$isPriority && `
    &::before {
      content: "PRIORITY";
      position: absolute;
      top: 12px;
      right: 18px;
      background: linear-gradient(135deg, #ffaa00, #ff8800);
      color: white;
      padding: 4px 10px;
      border-radius: 12px;
      font-size: 0.75rem;
      font-weight: bold;
      letter-spacing: 0.5px;
    }
  `}

  &:hover {
    border-color: ${props => props.$isPriority ? 'rgba(255, 170, 0, 0.8)' : 'rgba(0, 212, 255, 0.6)'};
    transform: translateY(-2px);
  }
`;

const NewsTitle = styled.h4`
  color: #00d4ff;
  margin: 0 0 8px 0;
  font-size: 1.1rem;
`;

const NewsMeta = styled.div`
  display: flex;
  gap: 15px;
  color: #888;
  font-size: 0.85rem;
  margin-bottom: 12px;
`;

const NewsContent = styled.p`
  color: #ccc;
  margin: 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const NewsStatus = styled.span<{ $published: boolean }>`
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: bold;
  text-transform: uppercase;
  ${props => props.$published ? `
    background: rgba(76, 175, 80, 0.2);
    color: #4caf50;
    border: 1px solid rgba(76, 175, 80, 0.4);
  ` : `
    background: rgba(255, 193, 7, 0.2);
    color: #ffc107;
    border: 1px solid rgba(255, 193, 7, 0.4);
  `}
`;

const NewsActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
  flex-wrap: wrap;
`;

const SmallButton = styled.button<{ $variant?: 'danger' | 'success' | 'default' }>`
  background: ${props => {
    switch (props.$variant) {
      case 'danger': return 'rgba(244, 67, 54, 0.15)';
      case 'success': return 'rgba(76, 175, 80, 0.15)';
      default: return 'rgba(0, 212, 255, 0.15)';
    }
  }};
  border: 1px solid ${props => {
    switch (props.$variant) {
      case 'danger': return 'rgba(244, 67, 54, 0.4)';
      case 'success': return 'rgba(76, 175, 80, 0.4)';
      default: return 'rgba(0, 212, 255, 0.4)';
    }
  }};
  color: ${props => {
    switch (props.$variant) {
      case 'danger': return '#f44336';
      case 'success': return '#4caf50';
      default: return '#00d4ff';
    }
  }};
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #999;
  font-style: italic;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;

  &::after {
    content: '';
    width: 40px;
    height: 40px;
    border: 3px solid rgba(0, 212, 255, 0.3);
    border-radius: 50%;
    border-top-color: #00d4ff;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.3);
  border-radius: 8px;
  padding: 20px;
  color: #f44336;
  text-align: center;
`;

type TabType = 'maps' | 'news' | 'games' | 'referees' | 'schedule';

const MainRefereeFieldManagePage: React.FC = () => {
  const navigate = useNavigate();
  const { referee, isRefereeAuthenticated, initializing } = useRefereeAuth();
  const { notifications, showNotification, removeNotification } = useNotification();
  const { confirm, modalProps } = useConfirmModal();
  const { hasFeature } = useSubscription();
  const hasAnalyticsAccess = hasFeature('analytics');

  // URL-based tabs
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('tab') as TabType) || 'maps';

  const setActiveTab = useCallback((tab: TabType) => {
    setSearchParams({ tab }, { replace: false });
  }, [setSearchParams]);

  const [fieldId, setFieldId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // News state
  const [news, setNews] = useState<FieldNewsDto[]>([]);
  const [newsLoading, setNewsLoading] = useState(false);
  const [isNewsModalOpen, setIsNewsModalOpen] = useState(false);
  const [editingNews, setEditingNews] = useState<FieldNewsDto | null>(null);
  const [selectedNews, setSelectedNews] = useState<FieldNewsDto | null>(null);

  // Referees state
  const [referees, setReferees] = useState<RefereeDto[]>([]);
  const [refereesLoading, setRefereesLoading] = useState(false);

  // Check if user is main referee
  useEffect(() => {
    console.log('MainRefereeFieldManagePage useEffect:', {
      initializing,
      isRefereeAuthenticated,
      referee,
      isMainReferee: referee?.isMainReferee
    });

    // Ждём пока закончится инициализация
    if (initializing) {
      return;
    }

    if (!isRefereeAuthenticated) {
      console.log('Not authenticated, redirecting to login');
      navigate('/login');
      return;
    }
    if (!referee?.isMainReferee) {
      console.log('Not main referee, redirecting to profile');
      showNotification('error', 'Access Denied', 'You are not a main referee.');
      navigate('/referee/profile');
      return;
    }
    console.log('Main referee verified, loading field ID');
    loadFieldId();
  }, [initializing, isRefereeAuthenticated, referee, navigate]);

  const loadFieldId = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await mainRefereeService.getFieldId();
      setFieldId(response.fieldId);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load field information');
      showNotification('error', 'Error', 'Failed to load field information');
    } finally {
      setLoading(false);
    }
  };

  // Load news when tab changes
  useEffect(() => {
    if (activeTab === 'news' && fieldId) {
      loadNews();
    }
  }, [activeTab, fieldId]);

  // Load referees when tab changes
  useEffect(() => {
    if ((activeTab === 'referees' || activeTab === 'schedule') && fieldId) {
      loadReferees();
    }
  }, [activeTab, fieldId]);

  const loadNews = async () => {
    try {
      setNewsLoading(true);
      const data = await mainRefereeService.getNews();
      setNews(data);
    } catch (err: any) {
      showNotification('error', 'Error', 'Failed to load news');
    } finally {
      setNewsLoading(false);
    }
  };

  const loadReferees = async () => {
    try {
      setRefereesLoading(true);
      const data = await mainRefereeService.getReferees();
      setReferees(data);
    } catch (err: any) {
      showNotification('error', 'Error', 'Failed to load referees');
    } finally {
      setRefereesLoading(false);
    }
  };

  const handleCreateNews = async (newsData: CreateNewsData) => {
    try {
      const formData = new FormData();
      formData.append('title', newsData.title);
      formData.append('content', newsData.content);
      formData.append('isPriority', String(newsData.isPriority));
      formData.append('isPublished', 'false');
      if (newsData.imageFile) {
        formData.append('imageFile', newsData.imageFile);
      }

      await mainRefereeService.createNews(formData);
      showNotification('success', 'Success', 'News created successfully');
      setIsNewsModalOpen(false);
      loadNews();
    } catch (err: any) {
      showNotification('error', 'Error', err.response?.data?.message || 'Failed to create news');
    }
  };

  const handleUpdateNews = async (newsData: CreateNewsData) => {
    if (!editingNews) return;

    try {
      const formData = new FormData();
      formData.append('title', newsData.title);
      formData.append('content', newsData.content);
      formData.append('isPriority', String(newsData.isPriority));
      formData.append('isPublished', String(editingNews.isPublished));
      if (newsData.imageFile) {
        formData.append('imageFile', newsData.imageFile);
      }

      await mainRefereeService.updateNews(editingNews.id, formData);
      showNotification('success', 'Success', 'News updated successfully');
      setEditingNews(null);
      loadNews();
    } catch (err: any) {
      showNotification('error', 'Error', err.response?.data?.message || 'Failed to update news');
    }
  };

  const handleDeleteNews = async (newsId: string) => {
    const confirmed = await confirm({
      title: 'Delete News',
      message: 'Are you sure you want to delete this news? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await mainRefereeService.deleteNews(newsId);
      showNotification('success', 'Success', 'News deleted successfully');
      loadNews();
    } catch (err: any) {
      showNotification('error', 'Error', err.response?.data?.message || 'Failed to delete news');
    }
  };

  const handlePublishNews = async (newsId: string) => {
    try {
      await mainRefereeService.publishNews(newsId);
      showNotification('success', 'Success', 'News published successfully');
      loadNews();
    } catch (err: any) {
      showNotification('error', 'Error', err.response?.data?.message || 'Failed to publish news');
    }
  };

  const handleUnpublishNews = async (newsId: string) => {
    try {
      await mainRefereeService.unpublishNews(newsId);
      showNotification('success', 'Success', 'News unpublished successfully');
      loadNews();
    } catch (err: any) {
      showNotification('error', 'Error', err.response?.data?.message || 'Failed to unpublish news');
    }
  };

  const handleUpdateRefereeStatus = async (refereeId: string, status: string) => {
    try {
      await mainRefereeService.updateRefereeStatus(refereeId, status);
      showNotification('success', 'Success', 'Referee status updated successfully');
      loadReferees();
    } catch (err: any) {
      showNotification('error', 'Error', err.response?.data?.message || 'Failed to update referee status');
    }
  };

  const handleNewsModalClose = () => {
    setIsNewsModalOpen(false);
    setEditingNews(null);
  };

  if (initializing || loading) {
    return (
      <Background>
        <Container>
          <LoadingSpinner />
        </Container>
      </Background>
    );
  }

  if (error) {
    return (
      <Background>
        <Container>
          <Header>
            <TitleSection>
              <Title>Field Management</Title>
              <Subtitle>Main Referee Access</Subtitle>
            </TitleSection>
            <BackButton onClick={() => navigate('/referee/profile')}>
              Back to Profile
            </BackButton>
          </Header>
          <ErrorMessage>{error}</ErrorMessage>
        </Container>
        <NotificationContainer
          notifications={notifications}
          onRemove={removeNotification}
        />
      </Background>
    );
  }

  return (
    <Background>
      <Container>
        <Header>
          <TitleSection>
            <Title>
              Field Management
              <span style={{ marginLeft: '12px', display: 'inline-flex', verticalAlign: 'middle' }}>
                <FieldInfoButton title="Main Referee Guide" sections={mainRefereeOverviewInfo} />
              </span>
            </Title>
            <Subtitle>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
              </svg>
              Main Referee Access
            </Subtitle>
          </TitleSection>
          <div style={{ display: 'flex', gap: '10px' }}>
            {hasAnalyticsAccess && fieldId && (
              <ActionButton onClick={() => navigate(`/fields/${fieldId}/analytics`)}>
                Analytics
              </ActionButton>
            )}
            <BackButton onClick={() => navigate('/referee/profile')}>
              Back to Profile
            </BackButton>
          </div>
        </Header>

        <TabContainer>
          <TabButton $active={activeTab === 'maps'} onClick={() => setActiveTab('maps')}>
            Maps & Nodes
          </TabButton>
          <TabButton $active={activeTab === 'news'} onClick={() => setActiveTab('news')}>
            News
          </TabButton>
          <TabButton $active={activeTab === 'games'} onClick={() => setActiveTab('games')}>
            Games
          </TabButton>
          <TabButton $active={activeTab === 'referees'} onClick={() => setActiveTab('referees')}>
            Referees
          </TabButton>
          <TabButton $active={activeTab === 'schedule'} onClick={() => setActiveTab('schedule')}>
            Schedule
          </TabButton>
        </TabContainer>

        {activeTab === 'maps' && fieldId && (
          <FieldMapsSection fieldId={fieldId} useMainRefereeApi={true} />
        )}

        {activeTab === 'news' && (
          <SectionCard>
            <SectionHeader>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <SectionTitle>Field News</SectionTitle>
                <FieldInfoButton title="News Management Help" sections={mainRefereeNewsInfo} />
              </div>
              <ActionButton onClick={() => setIsNewsModalOpen(true)}>
                Create News
              </ActionButton>
            </SectionHeader>
            <SectionContent>
              {newsLoading ? (
                <LoadingSpinner />
              ) : news.length === 0 ? (
                <EmptyState>No news yet. Create your first news article!</EmptyState>
              ) : (
                news.map(item => (
                  <NewsItem
                    key={item.id}
                    $isPriority={item.isPriority}
                    onClick={() => setSelectedNews(item)}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                      <NewsTitle>{item.title}</NewsTitle>
                      <NewsStatus $published={item.isPublished}>
                        {item.isPublished ? 'Published' : 'Draft'}
                      </NewsStatus>
                    </div>
                    <NewsMeta>
                      {item.authorName && <span>By {item.authorName}</span>}
                      <span>{new Date(item.createdDate).toLocaleDateString()}</span>
                    </NewsMeta>
                    <NewsContent>{item.content}</NewsContent>
                    <NewsActions onClick={(e) => e.stopPropagation()}>
                      <SmallButton onClick={() => setEditingNews(item)}>
                        Edit
                      </SmallButton>
                      {item.isPublished ? (
                        <SmallButton onClick={() => handleUnpublishNews(item.id)}>
                          Unpublish
                        </SmallButton>
                      ) : (
                        <SmallButton $variant="success" onClick={() => handlePublishNews(item.id)}>
                          Publish
                        </SmallButton>
                      )}
                      <SmallButton $variant="danger" onClick={() => handleDeleteNews(item.id)}>
                        Delete
                      </SmallButton>
                    </NewsActions>
                  </NewsItem>
                ))
              )}
            </SectionContent>
          </SectionCard>
        )}

        {activeTab === 'games' && fieldId && (
          <SectionCard>
            <SectionHeader>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <SectionTitle>Games</SectionTitle>
                <FieldInfoButton title="Games Help" sections={mainRefereeGamesInfo} />
              </div>
            </SectionHeader>
            <SectionContent>
              <GamesSection fieldId={fieldId} />
            </SectionContent>
          </SectionCard>
        )}

        {activeTab === 'referees' && (
          <SectionCard>
            <SectionHeader>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <SectionTitle>Field Referees</SectionTitle>
                <FieldInfoButton title="Referee Management Help" sections={mainRefereeRefereesInfo} />
              </div>
            </SectionHeader>
            <SectionContent>
              <RefereeList
                referees={referees}
                onUpdateStatus={handleUpdateRefereeStatus}
                loading={refereesLoading}
                restrictedMode={true}
                currentRefereeId={referee?.id}
              />
            </SectionContent>
          </SectionCard>
        )}

        {activeTab === 'schedule' && (
          <SectionCard>
            <SectionHeader>
              <SectionTitle>Field Schedule</SectionTitle>
            </SectionHeader>
            <SectionContent>
              <WeeklyCalendar
                useMainRefereeApi={true}
                canEdit={true}
                referees={referees
                  .filter(r => Object.values(r.fieldAssignments).includes('active'))
                  .map(r => ({ id: r.id, name: `${r.firstName} ${r.lastName}` }))}
              />
            </SectionContent>
          </SectionCard>
        )}
      </Container>

      {/* Create/Edit News Modal */}
      <CreateFieldNewsModal
        isOpen={isNewsModalOpen || editingNews !== null}
        onClose={handleNewsModalClose}
        onSubmit={editingNews ? handleUpdateNews : handleCreateNews}
        editingNews={editingNews}
      />

      {/* News Detail Modal */}
      <NewsDetailModal
        isOpen={selectedNews !== null}
        onClose={() => setSelectedNews(null)}
        news={selectedNews}
      />

      <NotificationContainer
        notifications={notifications}
        onRemove={removeNotification}
      />

      <ConfirmModal {...modalProps} />
    </Background>
  );
};

export default MainRefereeFieldManagePage;
